import React, { useEffect, useMemo, useState } from 'react';
import JWModal from '../brand/JWModal';
import { HighlightType, ProfileInfoType } from '../../types/profile';
import { AddButtonIcon, Checkmark, MinusCircle, PencilEditIcon } from '../icons';
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort';
import arrayMove from 'array-move';
import './highlight-edit.css';
import {
    isSpotifyTrack,
    isYouTubeVideo,
    isSoundCloudTrack,
    extractSpotifyTrackId,
    extractYouTubeVideoId,
    extractSoundCloudTrackUrl,
    isSpotifyPlaylist,
    extractSpotifyPlaylistId,
    extractSpotifyId
} from '../../utils/highlightUtil';
import { getTrackDetails, getPlaylistDetails, getAlbumDetails } from '../../api/spotify';
import axios from 'axios';
import { JamBoardPin } from '../../types/jamboard';
import { getHighlightReactKey } from '../../utils/highlightIdentity';

function currencySymbolForCode(code: string): string | null {
    const normalized = code.toUpperCase();
    if (normalized === 'USD') return '$';
    if (normalized === 'EUR') return '€';
    if (normalized === 'GBP') return '£';
    if (normalized === 'CAD') return 'C$';
    if (normalized === 'AUD') return 'A$';
    if (normalized === 'NZD') return 'NZ$';
    if (normalized === 'JPY') return '¥';
    return null;
}

function formatJamBoardRateLabel(
    paymentText: string | null | undefined,
    paymentCurrency: string | null | undefined
): string | undefined {
    const rawText = paymentText?.trim();
    if (!rawText) return 'FREE';
    const numericMatch = rawText.match(/\d+(?:[.,]\d+)?/);
    if (!numericMatch) {
        return 'FREE';
    }
    const numericValue = Number(numericMatch[0].replace(/,/g, ''));
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
        return 'FREE';
    }
    const formattedNumber = Number.isInteger(numericValue)
        ? numericValue.toLocaleString('en-US')
        : numericValue.toLocaleString('en-US', { maximumFractionDigits: 2 });
    const inlineCurrencyMatch = rawText.match(/\b(USD|EUR|GBP|CAD|AUD|NZD|JPY)\b/i);
    const resolvedCurrency = paymentCurrency || inlineCurrencyMatch?.[1] || 'USD';
    const symbol = currencySymbolForCode(resolvedCurrency) || '$';
    return `${symbol}${formattedNumber}`;
}

interface EditHighlightsModalProps {
    show: boolean,
    onClose: () => void,
    highlights?: HighlightType[] | undefined | null,
    deleteHighlight: (index: number) => void,
    saveHighlights: (highlights: HighlightType[], profileOverrides?: Partial<ProfileInfoType>) => void,
    profileInfo: ProfileInfoType | null | undefined,
    setProfileInfo: (profileInfo: any) => void,
    navigateToAddHighlightModal: () => void
    activeJamBoardPins?: JamBoardPin[]
    jamBoardPinsDisplayMode?: 'none' | 'most_recent' | 'all'
    hiddenJamBoardPinIds?: string[]
}

function EditHighlightsModal(props: EditHighlightsModalProps) {
    const [editedCaptions, setEditedCaptions] = useState<(boolean)[]>([]);
    const [items, setItems] = useState<HighlightType[]>([]);
    const [localProfileInfo, setLocalProfileInfo] = useState<ProfileInfoType | null | undefined>(null);
    const [isSorting, setIsSorting] = useState(false);
    const [jamBoardPinsDisplayMode, setJamBoardPinsDisplayMode] = useState<'none' | 'most_recent' | 'all'>('most_recent');
    const [hiddenJamBoardPinIds, setHiddenJamBoardPinIds] = useState<string[]>([]);
    const [isInitializedForOpen, setIsInitializedForOpen] = useState(false);

    useEffect(() => {
        if (!props.show) {
            setIsInitializedForOpen(false);
            return;
        }
        if (isInitializedForOpen) {
            return;
        }
        // Keep image file objects. JSON cloning drops File, and the next save
        // then stores the photo with no file and no imagePath.
        const detachedHighlights = (props.highlights || []).map((highlight) => ({ ...highlight }));
        setItems(detachedHighlights);
        if (props.profileInfo) {
            setLocalProfileInfo({
                ...props.profileInfo,
                highlights: detachedHighlights,
            });
        } else {
            setLocalProfileInfo(null);
        }
        setJamBoardPinsDisplayMode(props.jamBoardPinsDisplayMode || 'most_recent');
        setHiddenJamBoardPinIds(props.hiddenJamBoardPinIds || []);
        setIsInitializedForOpen(true);
    }, [props.show, isInitializedForOpen, props.profileInfo, props.highlights, props.jamBoardPinsDisplayMode, props.hiddenJamBoardPinIds]);

    const sortedActiveJamBoardPins = useMemo(
        () =>
            [...(props.activeJamBoardPins || [])].sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            ),
        [props.activeJamBoardPins]
    );

    const updateJamBoardSettings = (
        mode: 'none' | 'most_recent' | 'all',
        hiddenIds: string[]
    ) => {
        const normalizedHiddenIds = Array.from(
            new Set(
                hiddenIds
                    .map((id) => String(id))
                    .filter((id) => id.length > 0)
            )
        );
        const nextProfileInfo = {
            ...(localProfileInfo || props.profileInfo),
            jamBoardPinsDisplayMode: mode,
            hiddenJamBoardPinIds: normalizedHiddenIds,
        };
        setJamBoardPinsDisplayMode(mode);
        setHiddenJamBoardPinIds(normalizedHiddenIds);
        setLocalProfileInfo(nextProfileInfo as ProfileInfoType);
        props.setProfileInfo(nextProfileInfo);
    };

    const handleJamBoardModeChange = (
        mode: 'none' | 'most_recent' | 'all'
    ) => {
        updateJamBoardSettings(mode, mode === 'all' ? hiddenJamBoardPinIds : []);
    };

    const toggleJamBoardPinVisibility = (pinId: string) => {
        if (jamBoardPinsDisplayMode !== 'all') return;
        const pinIdStr = String(pinId);
        const hiddenIdSet = new Set(hiddenJamBoardPinIds.map((id) => String(id)));
        if (hiddenIdSet.has(pinIdStr)) {
            hiddenIdSet.delete(pinIdStr);
        } else {
            hiddenIdSet.add(pinIdStr);
        }
        const nextHiddenIds = Array.from(hiddenIdSet);
        updateJamBoardSettings('all', nextHiddenIds);
    };

    const jamBoardPinsForGrid = useMemo(() => {
        if (!sortedActiveJamBoardPins.length) {
            return [];
        }
        if (jamBoardPinsDisplayMode === 'none') {
            return [];
        }
        if (jamBoardPinsDisplayMode === 'most_recent') {
            return sortedActiveJamBoardPins.slice(0, 1);
        }
        return sortedActiveJamBoardPins;
    }, [sortedActiveJamBoardPins, jamBoardPinsDisplayMode]);

    const onSortEnd = (oldIndex: number, newIndex: number) => {
        setIsSorting(true);

        const source = (localProfileInfo?.highlights || items) as HighlightType[];
        const newItems = arrayMove(source, oldIndex, newIndex);
        setItems(newItems);
        if (localProfileInfo) {
            setLocalProfileInfo({ ...localProfileInfo, highlights: newItems });
        }

        props.saveHighlights(newItems);

        // Reset sorting mode after a short delay
        setTimeout(() => {
            setIsSorting(false);
        }, 50);
    };

    // Handle closing the modal - discard any unsaved changes
    const handleClose = () => {
        // Reset all editing states
        setEditedCaptions([]);

        // Persist JamBoard display settings once on close to avoid
        // request races while the user is actively toggling.
        const highlightsToSave =
            (localProfileInfo?.highlights || props.highlights || []) as HighlightType[];
        props.saveHighlights(highlightsToSave, {
            jamBoardPinsDisplayMode,
            hiddenJamBoardPinIds,
        });

        // Call the parent's onClose function
        props.onClose();
    };

    // start editing a caption
    const editCaptionClick = (index: number) => {
        const newEditedCaptions = [...editedCaptions];
        newEditedCaptions[index] = true;
        setEditedCaptions(newEditedCaptions);
        setTimeout(() => {
            document.getElementById(`caption-input-${index}`)?.focus();
        }, 100);
    };

    const handleEditCaption = (index: number, editedCaption: string) => {
        // Edit our local copy of the highlight data
        if (localProfileInfo?.highlights) {
            const modifiedHighlights = localProfileInfo.highlights.map((highlight: HighlightType, i: number) => {
                if (i === index) {
                    // If this is the object to be modified, create a new object with the updated description
                    return { ...highlight, description: editedCaption };
                }
                // If this is not the object to be modified, return the original object
                return highlight;
            });

            setLocalProfileInfo({ ...localProfileInfo, highlights: modifiedHighlights });
        }
    };

    // Format URL to ensure it has https:// prefix if needed
    const formatLink = (link: string): string => {
        if (!link) return '';
        if (link.startsWith('http://') || link.startsWith('https://')) return link;
        return `https://${link}`;
    };

    const handleEditUrl = async (index: number, editedUrl: string) => {
        // Edit our local copy of the highlight data
        if (localProfileInfo?.highlights) {
            const highlightPromises = localProfileInfo.highlights.map(async (highlight: HighlightType, i: number) => {
                if (i === index) {
                    // Format the URL to ensure it has https:// prefix if needed
                    const formattedUrl = formatLink(editedUrl);

                    // Determine if this is a Spotify or YouTube URL
                    const updatedHighlight = { ...highlight, url: formattedUrl };
                    let titleFetched = false;

                    // If it's a Spotify or YouTube URL, also update the mediaUrl and coverArtUrl
                    if (isSpotifyTrack(formattedUrl)) {
                        updatedHighlight.mediaUrl = formattedUrl;
                        updatedHighlight.typeCd = 'S';

                        // Get the track or playlist ID and fetch cover art and title
                        const spotifyData = extractSpotifyId(formattedUrl);
                        if (spotifyData.id) {
                            try {
                                if (spotifyData.type === 'playlist') {
                                    const playlistDetails = await getPlaylistDetails(spotifyData.id);
                                    if (playlistDetails?.coverArtUrl) {
                                        updatedHighlight.coverArtUrl = playlistDetails.coverArtUrl;
                                    }
                                    // If we have a title and there's no existing description or it matches a previous URL,
                                    // automatically populate the caption
                                    if (playlistDetails?.title &&
                                       (!updatedHighlight.description ||
                                        updatedHighlight.description === highlight.url ||
                                        updatedHighlight.description === highlight.mediaUrl)) {
                                        updatedHighlight.description = playlistDetails.title;
                                        titleFetched = true;
                                    }
                                } else if (spotifyData.type === 'album') {
                                    const albumDetails = await getAlbumDetails(spotifyData.id);
                                    if (albumDetails?.coverArtUrl) {
                                        updatedHighlight.coverArtUrl = albumDetails.coverArtUrl;
                                    }
                                    if (albumDetails?.title &&
                                       (!updatedHighlight.description ||
                                        updatedHighlight.description === highlight.url ||
                                        updatedHighlight.description === highlight.mediaUrl)) {
                                        const artistName = albumDetails.artist || '';
                                        const title = artistName
                                            ? `${artistName} - ${albumDetails.title}`
                                            : albumDetails.title;
                                        updatedHighlight.description = title;
                                        titleFetched = true;
                                    }
                                } else {
                                    const trackDetails = await getTrackDetails(spotifyData.id);
                                    if (trackDetails?.coverArtUrl) {
                                        updatedHighlight.coverArtUrl = trackDetails.coverArtUrl;
                                    }
                                    // If we have a title and artist, and there's no existing description or it matches a previous URL,
                                    // automatically populate the caption
                                    if (trackDetails?.title &&
                                       (!updatedHighlight.description ||
                                        updatedHighlight.description === highlight.url ||
                                        updatedHighlight.description === highlight.mediaUrl)) {
                                        const artistName = trackDetails.artist || '';
                                        const title = artistName
                                            ? `${artistName} - ${trackDetails.title}`
                                            : trackDetails.title;
                                        updatedHighlight.description = title;
                                        titleFetched = true;
                                    }
                                }
                            } catch (err) {
                                console.error(err);
                            }
                        }
                    } else if (isYouTubeVideo(formattedUrl)) {
                        updatedHighlight.mediaUrl = formattedUrl;
                        updatedHighlight.typeCd = 'Y';

                        // Get the YouTube video ID and set the cover art URL
                        const videoId = extractYouTubeVideoId(formattedUrl);
                        if (videoId) {
                            updatedHighlight.coverArtUrl = `https://i.ytimg.com/vi_webp/${videoId}/mqdefault.webp`;

                            // Try to fetch the YouTube video title using the oEmbed API
                            try {
                                const response = await axios.get(
                                    `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
                                );
                                // If we got a title and there's no existing description or it matches a previous URL,
                                // automatically populate the caption
                                if (response.data.title &&
                                   (!updatedHighlight.description ||
                                    updatedHighlight.description === highlight.url ||
                                    updatedHighlight.description === highlight.mediaUrl)) {
                                    updatedHighlight.description = response.data.title;
                                    titleFetched = true;
                                }
                            } catch (err) {
                                console.error('Error fetching YouTube title:', err);
                            }
                        }
                    } else if (isSoundCloudTrack(formattedUrl)) {
                        updatedHighlight.mediaUrl = formattedUrl;
                        updatedHighlight.typeCd = 'SC';

                        // Try to fetch SoundCloud track artwork and title using oEmbed API
                        try {
                            const response = await axios.get(
                                `https://soundcloud.com/oembed?url=${encodeURIComponent(formattedUrl)}&format=json`
                            );
                            if (response.data.thumbnail_url) {
                                updatedHighlight.coverArtUrl = response.data.thumbnail_url;
                            }
                            // If we have a title and there's no existing description or it matches a previous URL,
                            // automatically populate the caption
                            if (response.data.title &&
                               (!updatedHighlight.description ||
                                updatedHighlight.description === highlight.url ||
                                updatedHighlight.description === highlight.mediaUrl)) {
                                // Extract artist and track title from the oEmbed title
                                const titleParts = response.data.title.split(' by ');
                                if (titleParts.length === 2) {
                                    updatedHighlight.description = `${titleParts[1]} - ${titleParts[0]}`;
                                } else {
                                    updatedHighlight.description = response.data.title;
                                }
                                titleFetched = true;
                            }
                        } catch (err) {
                            console.error('Error fetching SoundCloud artwork and title:', err);
                        }
                    }

                    // If we're in edit mode and successfully fetched a title, automatically switch to edit mode
                    // to show the user we've populated the caption for them
                    if (titleFetched && !editedCaptions[index]) {
                        const newEditedCaptions = [...editedCaptions];
                        newEditedCaptions[index] = true;
                        setEditedCaptions(newEditedCaptions);
                    }

                    return updatedHighlight;
                }
                // If this is not the object to be modified, return the original object
                return highlight;
            });

            // Resolve all promises (for async Spotify API calls)
            const modifiedHighlights = await Promise.all(highlightPromises);

            // Update our local copy only
            setLocalProfileInfo({ ...localProfileInfo, highlights: modifiedHighlights });
        }
    };

    const handleDeleteHighlight = (index: number) => {
        const sourceHighlights =
            (localProfileInfo?.highlights || items || []) as HighlightType[];
        if (index < 0 || index >= sourceHighlights.length) {
            return;
        }

        const modifiedHighlights = [...sourceHighlights];
        modifiedHighlights.splice(index, 1);

        setItems(modifiedHighlights);
        setEditedCaptions((prevCaptions) =>
            prevCaptions.filter((_, i) => i !== index)
        );

        setLocalProfileInfo((prevProfileInfo) => {
            if (prevProfileInfo) {
                return { ...prevProfileInfo, highlights: modifiedHighlights };
            }
            if (props.profileInfo) {
                return {
                    ...props.profileInfo,
                    highlights: modifiedHighlights,
                } as ProfileInfoType;
            }
            return prevProfileInfo;
        });

        props.saveHighlights(modifiedHighlights);
    };

    const confirmCaptionClick = (index: number) => {
        const newEditedCaptions = [...editedCaptions];
        newEditedCaptions[index] = false;
        setEditedCaptions(newEditedCaptions);

        // Save the changes from our local copy to the parent component
        if (localProfileInfo?.highlights) {
            // Update the parent's state with our edited version
            props.setProfileInfo(localProfileInfo);

            // Save the highlights
            props.saveHighlights(localProfileInfo.highlights);
        }
    };

    const modalBody = items?.length || sortedActiveJamBoardPins.length ? (
        <div className="w-full h-full flex flex-col">
            <div className="p-2 sticky top-0 z-10">
                <div className="relative">
                    <h2 className="text-xl font-bold text-center mb-2">Edit Highlights</h2>
                    <div className="text-sm text-gray-600 mb-3 text-center">
                        Drag to reorder. Delete highlights. Edit captions/links.
                    </div>
                    <div className="absolute top-0 right-0">
                        <div className={`text-sm font-medium rounded-full px-3 py-1 ${items.length >= 30 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}>
                            {items.length}/30 {items.length >= 30 && '⚠️'}
                        </div>
                    </div>
                </div>
            </div>
            {sortedActiveJamBoardPins.length > 0 && (
                <div className="px-2 pb-2">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <label
                            htmlFor="jamboard-pins-display-mode-select"
                            className="block text-sm font-semibold text-gray-800 mb-2"
                        >
                            Show JamBoard pins?
                        </label>
                        <select
                            id="jamboard-pins-display-mode-select"
                            className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
                            value={jamBoardPinsDisplayMode}
                            onChange={(e) =>
                                handleJamBoardModeChange(
                                    e.target.value as 'none' | 'most_recent' | 'all'
                                )
                            }
                        >
                            <option value="none">None</option>
                            <option value="most_recent">Most Recent</option>
                            <option value="all">All</option>
                        </select>
                    </div>
                </div>
            )}
            <div className="p-2 pb-6 overflow-y-auto flex-grow">
                <SortableList
                    onSortEnd={onSortEnd}
                    className={`flex flex-wrap gap-4 justify-center md:grid md:grid-cols-3 md:gap-5 md:max-w-[650px] md:mx-auto ${isSorting ? 'sorting-complete' : ''}`}
                    draggedItemClassName="highlight-dragged"
                >
                    {jamBoardPinsForGrid.map((pin) => {
                        const isHidden = hiddenJamBoardPinIds.some(
                            (id) => String(id) === String(pin._id)
                        );
                        const rateLabel = formatJamBoardRateLabel(
                            pin.paymentText,
                            pin.paymentCurrency
                        );
                        const isVisible = jamBoardPinsDisplayMode === 'all' ? !isHidden : true;
                        const isToggleMode = jamBoardPinsDisplayMode === 'all';
                        const cardClassName = `relative w-[180px] md:w-full bg-white rounded-lg shadow-md overflow-hidden ${isVisible ? 'ring-2 ring-purple/40' : 'opacity-70'}`;

                        if (isToggleMode) {
                            return (
                                <button
                                    key={`jamboard-pin-${pin._id}`}
                                    type="button"
                                    className={cardClassName}
                                    onClick={() => toggleJamBoardPinVisibility(pin._id)}
                                >
                                    <div className="relative h-[180px] overflow-hidden bg-gray-100">
                                        {pin.projectImage ? (
                                            <img
                                                draggable="false"
                                                className="object-cover w-full h-full"
                                                src={pin.projectImage}
                                                alt={pin.title || 'JamBoard Pin'}
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center px-3 bg-gradient-to-br from-[#f1ebff] to-[#e2dcff]">
                                                <span className="text-xs font-semibold text-purple text-center line-clamp-3">
                                                    {pin.title || 'JamBoard Pin'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    {rateLabel && (
                                        <div className="absolute top-2 left-2 z-10 rounded-full border border-white/40 bg-white/80 px-2.5 py-1 shadow-sm backdrop-blur-sm">
                                            <span className="text-xs font-semibold tracking-wide text-[#1f2937]">
                                                {rateLabel}
                                            </span>
                                        </div>
                                    )}
                                    <span className={`absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded border shadow-sm ${isVisible ? 'border-purple bg-purple text-white' : 'border-gray-300 bg-white'}`}>
                                        {isVisible && <Checkmark width={12} height={12} />}
                                    </span>
                                    <div className="p-3 text-left">
                                        <div className="text-xs text-gray-500 mb-1">JamBoard Pin</div>
                                        <div className="text-sm font-medium text-gray-900 line-clamp-2 pr-7">
                                            {pin.title}
                                        </div>
                                    </div>
                                </button>
                            );
                        }

                        return (
                            <div
                                key={`jamboard-pin-${pin._id}`}
                                className={cardClassName}
                            >
                                <div className="relative h-[180px] overflow-hidden bg-gray-100">
                                    {pin.projectImage ? (
                                        <img
                                            draggable="false"
                                            className="object-cover w-full h-full"
                                            src={pin.projectImage}
                                            alt={pin.title || 'JamBoard Pin'}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center px-3 bg-gradient-to-br from-[#f1ebff] to-[#e2dcff]">
                                            <span className="text-xs font-semibold text-purple text-center line-clamp-3">
                                                {pin.title || 'JamBoard Pin'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                {rateLabel && (
                                    <div className="absolute top-2 left-2 z-10 rounded-full border border-white/40 bg-white/80 px-2.5 py-1 shadow-sm backdrop-blur-sm">
                                        <span className="text-xs font-semibold tracking-wide text-[#1f2937]">
                                            {rateLabel}
                                        </span>
                                    </div>
                                )}
                                <span className="absolute right-2 top-2 inline-flex h-5 items-center justify-center rounded border border-purple bg-purple px-2 text-[10px] font-semibold text-white shadow-sm">
                                    Most Recent
                                </span>
                                <div className="p-3 text-left">
                                    <div className="text-xs text-gray-500 mb-1">JamBoard Pin</div>
                                    <div className="text-sm font-medium text-gray-900 line-clamp-2 pr-7">
                                        {pin.title}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {/* Only show add button if we're below the 30 highlights limit */}
                    {(!items || items.length < 30) && (
                        <div
                            className="w-[180px] md:w-full h-[260px] bg-white rounded-lg border-2 border-dashed border-gray-300 overflow-hidden flex flex-col items-center justify-center hover:border-purple transition-colors cursor-pointer transition-transform duration-300 ease-in-out hover:scale-105"
                            onClick={props.navigateToAddHighlightModal}
                        >
                            <AddButtonIcon
                                width={40}
                                height={40}
                                fill={'#262262'}
                                fillOpacity={1}
                            />
                            <p className="mt-4 text-gray-700 font-medium">Add to Highlights</p>
                        </div>
                    )}
                    {items.map((highlightItem, index) => {
                        // Get the data from our local copy for the editor
                        const displayItem = localProfileInfo?.highlights?.[index] || highlightItem;

                        return (
                            <SortableItem key={getHighlightReactKey(highlightItem, index)}>
                                <div className="relative w-[180px] md:w-full bg-white rounded-lg shadow-md overflow-hidden highlight-card">
                                    <button
                                        type="button"
                                        className="absolute cursor-pointer right-2 top-2 z-10 bg-white rounded-full p-1 shadow-md hover:bg-gray-100 transition-colors"
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            handleDeleteHighlight(index);
                                        }}
                                        aria-label="Delete highlight"
                                    >
                                        <MinusCircle width={24} height={24} />
                                    </button>
                                    <SortableKnob>
                                        <div className="h-[180px] overflow-hidden bg-gray-100 cursor-move">
                                            <img
                                                draggable="false"
                                                className="object-cover w-full h-full"
                                                src={
                                                    displayItem?.image?.url ||
                                                displayItem?.coverArtUrl ||
                                                ''
                                                }
                                                alt={displayItem?.description || 'Highlight'}
                                            />
                                        </div>
                                    </SortableKnob>
                                    <div className="p-3">
                                        {editedCaptions[index] ? (
                                            <div className="space-y-2">
                                                <div className="text-xs text-gray-500 font-medium">Caption</div>
                                                <input
                                                    id={`caption-input-${index}`}
                                                    className="w-full text-sm p-2 border border-gray-300 rounded"
                                                    value={displayItem?.description}
                                                    placeholder="Add a caption"
                                                    onChange={(e) =>
                                                        handleEditCaption(
                                                            index,
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                                <div className="text-xs text-gray-500 font-medium mt-2">URL Link</div>
                                                <input
                                                    id={`caption-url-input-${index}`}
                                                    className="w-full text-sm p-2 border border-gray-300 rounded"
                                                    value={displayItem?.url}
                                                    placeholder="Add a URL"
                                                    onChange={(e) => {
                                                        handleEditUrl(index, e.target.value);
                                                    }}
                                                    onBlur={(e) => {
                                                        if (e.target.value) {
                                                            handleEditUrl(index, formatLink(e.target.value));
                                                        }
                                                    }}
                                                />
                                                <div className="flex justify-end mt-3">
                                                    <button
                                                        onClick={() => confirmCaptionClick(index)}
                                                        className="bg-purple text-white px-4 py-2 rounded-full flex items-center text-sm hover:bg-purple-700 transition-colors"
                                                    >
                                                        <Checkmark width={14} height={14} className="mr-1" />
                                                    Save
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                {displayItem?.description ? (
                                                    <div
                                                        className="text-purple text-sm line-clamp-2 mb-2 hover:underline block cursor-pointer"
                                                        onClick={() => editCaptionClick(index)}
                                                    >
                                                        {displayItem.description}
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="text-gray-400 text-sm mb-2 italic cursor-pointer"
                                                        onClick={() => editCaptionClick(index)}
                                                    >
                                                    No caption
                                                    </div>
                                                )}
                                                <button
                                                    className="flex text-sm items-center text-gray-600 hover:text-purple transition-colors hover:bg-transparent"
                                                    onClick={() => editCaptionClick(index)}
                                                >
                                                    <PencilEditIcon
                                                        width={16}
                                                        height={16}
                                                        fill="currentColor"
                                                    />
                                                    <span className="ml-2">Edit Caption</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </SortableItem>
                        );
                    })}
                </SortableList>
            </div>
        </div>
    ) : (
        <div className="w-full h-full flex flex-col">
            <div className="p-2 sticky top-0 z-10">
                <div className="relative">
                    <h2 className="text-xl font-bold text-center mb-2">Edit Highlights</h2>
                    <div className="text-sm text-gray-600 mb-3 text-center">
                        Drag to reorder. Delete highlights. Edit captions/links.
                    </div>
                    <div className="absolute top-0 right-0">
                        <div className={`text-sm font-medium rounded-full px-3 py-1 ${items.length >= 30 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}>
                            {items.length}/30 {items.length >= 30 && '⚠️'}
                        </div>
                    </div>
                </div>
            </div>
            <div className="p-6 flex justify-center items-center flex-grow">
                <p>No highlights available</p>
            </div>
        </div>
    );

    return (
        <JWModal
            show={props.show}
            onClose={handleClose}
            wrapperClassName={'md:w-[710px] w-[95%] h-auto max-h-[85vh]'}
        >
            {modalBody}
        </JWModal>
    );
}

export default EditHighlightsModal;
